import { filter, find, isUndefined, map, values } from 'lodash-es'
import type { GqlClient } from '@/types/gqlClient'
import { graphql } from 'gql.tada'
import { gql } from 'urql'
import { EnumClass, ProjectStep } from 'src/utils/enums'
import {
  ProjectActionStatus,
  ProjectPlot,
  ProjectStatus,
  SignatoryClientRow,
} from 'types'
import { withPlotLocalities } from './project_plots'
import type { StudyEditor } from '@autoriz/shared/planning'
import { emailMatchPattern, normalizeEmail } from '@autoriz/shared/validation'

/**
 * A client row shaped for a DIRECT nested Hasura insert.
 *
 * Still used by the flows that were not moved to an action in #655: creating a
 * client on its own (`insert_clients_one`) and adding a co-signatory to an
 * existing project (`addAdditionalSignatoryClient`). Both keep their `clients`
 * insert permission — only project CREATION closed.
 */
export type ClientInsertInput = {
  data: {
    gender: EnumClass
    first_name: string
    last_name: string
    phone?: string
    mobile_phone?: string
    email_address?: string
    street_number?: string
    street_name?: string
    named_place?: string
    postal_code?: string
    city?: string
    electronic_administration_documents: boolean
    birth_date?: string
    birth_place?: string
    birth_postal_code?: string
    birth_country?: string
    company: boolean
    corporate_name?: string
    social_denomination?: string
    society_type?: string
    siret?: string
    account?: {
      data: {
        email: string
        account_type: string
      }
    }
  }
}

/**
 * One declarer, in the shape `create_project` takes (#655).
 *
 * The old nested mutation sent `Int | clients_insert_input` and rewrote its own
 * variable declarations at runtime to type it. GraphQL input types have no
 * unions, so the action flattens both cases into one shape and the server
 * enforces the either/or; `existing_client_id` alone means "reuse this client".
 */
export type ProjectClientInput = {
  existing_client_id?: number

  gender?: string
  first_name?: string
  last_name?: string
  phone?: string
  mobile_phone?: string
  email_address?: string
  street_number?: string
  street_name?: string
  named_place?: string
  postal_code?: string
  city?: string
  electronic_administration_documents?: boolean

  birth_date?: string
  birth_place?: string
  birth_postal_code?: string
  birth_country?: string

  company?: boolean
  corporate_name?: string
  social_denomination?: string
  society_type?: string
  siret?: string

  with_account?: boolean
}

/**
 * A declarer for a DIRECT nested insert — the shape `addAdditionalSignatoryClient`
 * and the standalone client-create flow still send.
 *
 * Kept as it was: only project CREATION moved behind an action in #655, and the
 * `clients` insert permission that backs these two flows is deliberately still
 * open to commercials. `gender` stays wrapped in `EnumClass` because these
 * documents are hand-built strings where the enum must render unquoted.
 */
export const formatClientUserInput = (
  client: SignatoryClientRow
): ClientInsertInput | number => {
  if (client.existingClient) {
    return client.client.id
  }
  const common: ClientInsertInput['data'] = {
    gender: new EnumClass(client.client.gender),
    first_name: client.client.first_name,
    last_name: client.client.last_name,
    phone: client.client.phone,
    mobile_phone: client.client.mobile_phone,
    email_address: client.client.email_address,
    street_number: client.client.street_number,
    street_name: client.client.street_name,
    named_place: client.client.named_place,
    postal_code: client.client.postal_code,
    city: client.client.city,
    electronic_administration_documents:
      client.client.electronic_administration_documents,
    company: client.client.company,
    ...(client.withAccount
      ? {
          account: {
            data: {
              email: normalizeEmail(client.client.email_address as string),
              account_type: 'CLIENT',
            },
          },
        }
      : {}),
  }
  if (client.client.company) {
    return {
      data: {
        ...common,
        company: client.client.company,
        corporate_name: client.client.corporate_name,
        social_denomination: client.client.social_denomination,
        society_type: client.client.society_type,
        siret: client.client.siret,
      },
    }
  } else {
    return {
      data: {
        ...common,
        birth_date: client.client.birth_date,
        birth_place: client.client.birth_place,
        birth_postal_code: client.client.birth_postal_code,
        birth_country: client.client.birth_country,
      },
    }
  }
}

/**
 * The same declarer, shaped for the `create_project` action (#655).
 *
 * Separate from `formatClientUserInput` on purpose. The action takes JSON —
 * plain strings, one flat object, `existing_client_id` alone for a reused
 * client — while the direct-insert path takes a nested `{ data: … }` with the
 * `EnumClass` wrapper. Merging them would mean one function whose output is
 * wrong for one of its two callers.
 */
export const formatProjectClientInput = (
  client: SignatoryClientRow
): ProjectClientInput => {
  // An existing client travels as an id ALONE — the server rejects an id sent
  // with stray fields rather than silently dropping them.
  if (client.existingClient) {
    return { existing_client_id: client.client.id }
  }
  const common: ProjectClientInput = {
    gender: client.client.gender,
    first_name: client.client.first_name,
    last_name: client.client.last_name,
    phone: client.client.phone,
    mobile_phone: client.client.mobile_phone,
    email_address: client.client.email_address,
    street_number: client.client.street_number,
    street_name: client.client.street_name,
    named_place: client.client.named_place,
    postal_code: client.client.postal_code,
    city: client.client.city,
    electronic_administration_documents:
      client.client.electronic_administration_documents,
    company: client.client.company,
    ...(client.withAccount ? { with_account: true } : {}),
  }
  if (client.client.company) {
    return {
      ...common,
      corporate_name: client.client.corporate_name,
      social_denomination: client.client.social_denomination,
      society_type: client.client.society_type,
      siret: client.client.siret,
    }
  }
  return {
    ...common,
    birth_date: client.client.birth_date,
    birth_place: client.client.birth_place,
    birth_postal_code: client.client.birth_postal_code,
    birth_country: client.client.birth_country,
  }
}

export type CreateProjectData = {
  project_type: string
  street_number?: string
  street_name?: string
  named_place?: string
  postal_code?: string
  city?: string
  plots: Array<{
    main_plot: boolean
    prefix?: string
    section?: string
    number?: string
    area?: number
    locality: string
    department_code?: string | null
  }>
  primary_signatory_client: ProjectClientInput
  additional_signatory_clients: Array<ProjectClientInput>
  salesman_id?: number
  /** The standalone study the project is made from; the server links it. */
  urban_analysis_id?: number
}
type CreateProjectResponse = {
  create_project: { id: number }
}
export const formatCreateProjectData = async (
  gqlClient: GqlClient,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formValues: any,
  clients: Array<SignatoryClientRow>,
  selectedPlots: Array<ProjectPlot>,
  mainPlotSlug: string | undefined
): Promise<CreateProjectData> => {
  const primaryClient = find(clients, 'primary')
  if (isUndefined(primaryClient)) {
    throw Error('No primary client')
  }
  const additionalClients = filter(clients, { primary: false })

  // The commune is what picks the PLU, so a plot the cadastre cannot place in
  // one commune stops the creation here (#746).
  const plots = await withPlotLocalities(
    gqlClient,
    selectedPlots,
    formValues.postal_code
  )
  // Note: code is now a generated column, so it's not included in the insert
  const plotsData = map(plots, (plot) => ({
    main_plot: mainPlotSlug == plot.slug,
    prefix: plot.prefix,
    section: plot.section,
    number: plot.number,
    area: plot.area,
    locality: plot.locality,
    department_code: plot.department_code,
  }))

  // The `create_project` action input (#655). Plot locality is still resolved
  // here rather than server-side, mirroring the urban-analysis action, whose
  // UrbanAnalysisPlotInput has always taken a resolved locality.
  return {
    project_type: formValues.project_type,
    street_number: formValues.street_number,
    street_name: formValues.street_name,
    named_place: formValues.named_place,
    postal_code: formValues.postal_code,
    city: formValues.city,
    plots: plotsData,
    primary_signatory_client: formatProjectClientInput(primaryClient),
    additional_signatory_clients: map(additionalClients, (item) =>
      formatProjectClientInput(item)
    ),
    salesman_id: formValues.salesman,
  }
}
export const createProject = (
  gqlClient: GqlClient,
  data: CreateProjectData
): Promise<CreateProjectResponse> => {
  if (isUndefined(data.project_type)) {
    throw new Error(
      'createProject: a project_type (PERGOLA, POOL or VERANDA) is required'
    )
  }
  // Project creation runs through the `create_project` Hasura Action (#655).
  //
  // It used to be a nested `insert_projects` mutation sent straight from here,
  // with its own query text built by string interpolation because gql.tada
  // overflows on the recursive *_insert_input types. That trick is gone: the
  // action's input is a fixed shape, so this is a plain document.
  //
  // The move is what lets ONE server-side check cover both kinds of creation —
  // and it is why the `projects` insert permission is now closed to SALESMAN
  // and SOCIETY. This is their only way in.
  const query = gql(`
    mutation CreateProject(
      $project_type: String!
      $street_number: String
      $street_name: String
      $named_place: String
      $postal_code: String
      $city: String
      $salesman_id: Int
      $urban_analysis_id: Int
      $plots: [ProjectPlotInput!]!
      $primary_signatory_client: ProjectClientInput!
      $additional_signatory_clients: [ProjectClientInput!]!
    ) {
      create_project(
        project_type: $project_type
        street_number: $street_number
        street_name: $street_name
        named_place: $named_place
        postal_code: $postal_code
        city: $city
        salesman_id: $salesman_id
        urban_analysis_id: $urban_analysis_id
        plots: $plots
        primary_signatory_client: $primary_signatory_client
        additional_signatory_clients: $additional_signatory_clients
      ) {
        id
      }
    }
  `)
  return gqlClient.request<CreateProjectResponse>(query, { ...data })
}
export const getProjectStatus = (
  gqlClient: GqlClient,
  projectID: number
): Promise<ProjectStatus> => {
  const query = graphql(`
    query ($projectID: Int!) {
      project_section_status(projectID: $projectID) {
        project_status {
          ${map(
            [
              'EXISTING_BUILDING',
              'DECLARER',
              'LAND',
              'PERGOLA',
              'PROJECT',
              'VERANDA',
              'POOL',
              'URBAN_ANALYSIS',
              'DP',
              'PC',
              'PCM',
            ],
            (formName) => `
          ${formName} {
            errors {
              type
              field_name
              context
            }
            warnings {
              type
              field_name
              context
            }
            status
          }
          `
          )}
        }
      }
    }
  `)
  return gqlClient
    .request(query, {
      projectID: projectID,
    })
    .then((data: unknown) => {
      const result = data as { project_section_status: ProjectStatus }
      return Promise.resolve(result.project_section_status)
    })
}
export const projectFormAction = (
  gqlClient: GqlClient,
  projectID: number,
  formName: string,
  status: string,
  comment: string | null,
  /**
   * Which editor a study was validated from — decides which report the study
   * files (#662). Meaningful only for formName "URBAN_ANALYSIS"; every other
   * section leaves it undefined and the action ignores it.
   */
  editor?: StudyEditor
): Promise<ProjectStatus> => {
  const query = graphql(`
    mutation (
      $formName: String!
      $projectID: Int!
      $status: String!
      $comment: String
      $editor: String
    ) {
      project_form_action(
        formName: $formName
        projectID: $projectID
        status: $status
        comment: $comment
        editor: $editor
      ) {
        errors {
          type
          field_name
          context
        }
        warnings {
          type
          field_name
          context
        }
        status
      }
    }
  `)
  return gqlClient
    .request(query, {
      projectID: projectID,
      formName: formName,
      status: status,
      comment: comment,
      editor: editor ?? null,
    })
    .then((data: unknown) => {
      const result = data as { project_form_action: ProjectStatus }
      return Promise.resolve(result.project_form_action)
    })
}
export const projectAction = (
  gqlClient: GqlClient,
  projectID: number,
  action: string,
  data: object = {}
): Promise<ProjectActionStatus> => {
  const query = graphql(`
    mutation ($projectID: Int!, $actionName: String!, $data: jsonb) {
      project_action(
        actionName: $actionName
        projectID: $projectID
        data: $data
      ) {
        errors
        data
        step
      }
    }
  `)
  return gqlClient
    .request(query, {
      projectID: projectID,
      actionName: action,
      data: data,
    })
    .then((data: unknown) => {
      const result = data as { project_action: ProjectActionStatus }
      return Promise.resolve(result.project_action)
    })
}
export const searchExistingClient = (
  gqlClient: GqlClient,
  firstName: string,
  lastName: string
): Promise<Array<{ id: number }>> => {
  const query = graphql(`
    query ($firstName: String!, $lastName: String!) {
      clients(
        where: {
          first_name: { _eq: $firstName }
          last_name: { _eq: $lastName }
        }
      ) {
        id
      }
    }
  `)
  return gqlClient
    .request(query, {
      firstName: firstName,
      lastName: lastName,
    })
    .then((data) => Promise.resolve(data.clients as Array<{ id: number }>))
}
export const searchExistingAccount = (
  gqlClient: GqlClient,
  email: string
): Promise<Array<{ id: number }>> => {
  // Case-insensitive: an account exists whatever the case it was stored in.
  const query = graphql(`
    query ($pattern: String!) {
      accounts(where: { email: { _ilike: $pattern } }) {
        id
      }
    }
  `)
  return gqlClient
    .request(query, {
      pattern: emailMatchPattern(email),
    })
    .then((data) => Promise.resolve(data.accounts as Array<{ id: number }>))
}
export const addAdditionalSignatoryClient = (
  gqlClient: GqlClient,
  project_id: number,
  client: ClientInsertInput | number
): Promise<{ client_id: number; project_id: number }> => {
  // This query needs to remain as a template string due to dynamic field selection
  const query = `
  mutation (
    $project_id: Int!,
    ${
      Number.isInteger(client)
        ? '$client: Int'
        : '$client: clients_obj_rel_insert_input'
    }
  ) {
    insert_project_additional_signatory_clients_one(object: {
      project_id: $project_id,
      ${Number.isInteger(client) ? 'client_id: $client' : 'client: $client'}
    }) {
      client_id
      project_id
    }
  }
  `
  return gqlClient
    .request(query, {
      project_id: project_id,
      client: client,
    })
    .then((data: unknown) => {
      const result = data as {
        insert_project_additional_signatory_clients_one: {
          client_id: number
          project_id: number
        }
      }
      return Promise.resolve(
        result.insert_project_additional_signatory_clients_one
      )
    })
}
export const switchPrimarySignatoryClient = (
  gqlClient: GqlClient,
  project_id: number,
  prev_primary_client_id: number,
  new_primary_client_id: number
) => {
  // This query needs to remain as a template string due to multiple operations
  const query = `
  mutation (
    $project_id: Int!,
    $prev_primary_client_id: Int!,
    $new_primary_client_id: Int!
  ) {
    insert_project_additional_signatory_clients_one(object: {
      project_id: $project_id,
      client_id: $prev_primary_client_id
    }) {
      client_id
      project_id
    }
    update_projects_by_pk(
      pk_columns: {id: $project_id},
      _set: {primary_signatory_client_id: $new_primary_client_id}
    ) {
      primary_signatory_client_id
    }
    delete_project_additional_signatory_clients_by_pk(client_id: $new_primary_client_id, project_id: $project_id) {
      client_id
      project_id
    }
  }
  `
  return gqlClient.request(query, {
    project_id: project_id,
    prev_primary_client_id: prev_primary_client_id,
    new_primary_client_id: new_primary_client_id,
  })
}
export const removeAdditionalSignatoryClient = (
  gqlClient: GqlClient,
  project_id: number,
  client_id: number
): Promise<{ client_id: number; project_id: number }> => {
  const query = graphql(`
    mutation ($client_id: Int!, $project_id: Int!) {
      delete_project_additional_signatory_clients_by_pk(
        client_id: $client_id
        project_id: $project_id
      ) {
        client_id
        project_id
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: project_id,
      client_id: client_id,
    })
    .then((data) => {
      if (!data.delete_project_additional_signatory_clients_by_pk) {
        throw new Error('Failed to remove additional signatory client')
      }
      return Promise.resolve(
        data.delete_project_additional_signatory_clients_by_pk
      )
    })
}
export const updatePC = (
  gqlClient: GqlClient,
  projectID: number,
  values: {
    status?: 'COMPLETED' | 'EDITED' | 'EMPTY' | 'REFUSED' | 'VALIDATED' | null
    project_info_checked?: unknown
    project_description?: string | null
    project_id?: number | null
    floor_area_before_work?: number | null
    floor_area_created?: number | null
    floor_area_removed?: number | null
  }
): Promise<{ project_id: number }> => {
  const query = graphql(`
    mutation ($project_id: Int!, $set: pc_set_input) {
      update_pc_by_pk(pk_columns: { project_id: $project_id }, _set: $set) {
        project_id
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
      set: values,
    })
    .then((data) => {
      if (!data.update_pc_by_pk) {
        throw new Error('Failed to update PC')
      }
      return Promise.resolve(data.update_pc_by_pk)
    })
}
export const updateDP = (
  gqlClient: GqlClient,
  projectID: number,
  values: {
    status?: 'COMPLETED' | 'EDITED' | 'EMPTY' | 'REFUSED' | 'VALIDATED' | null
    project_info_checked?: unknown
    project_description?: string | null
    project_id?: number | null
    floor_area_before_work?: number | null
    floor_area_created?: number | null
    floor_area_removed?: number | null
  }
): Promise<{ project_id: number }> => {
  const query = graphql(`
    mutation ($project_id: Int!, $set: dp_set_input) {
      update_dp_by_pk(pk_columns: { project_id: $project_id }, _set: $set) {
        project_id
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
      set: values,
    })
    .then((data) => {
      if (!data.update_dp_by_pk) {
        throw new Error('Failed to update DP')
      }
      return Promise.resolve(data.update_dp_by_pk)
    })
}
export type CountByStep = Record<
  string,
  {
    aggregate: {
      count: number
    }
  }
>
export const countProjectByStep = (
  gqlClient: GqlClient
): Promise<CountByStep> => {
  // This query needs to remain as a template string due to dynamic field generation
  const query = `
  query {
    ${values(ProjectStep).map(
      (step) =>
        `
      ${step}: projects_aggregate(where: {step: {_eq: ${step}}, enabled: {_eq: true}}) {
        aggregate {
          count
        }
      }
        `
    )}
  }
    `
  return gqlClient.request(query)
}
export const updateDepositeNumber = (
  gqlClient: GqlClient,
  projectID: number,
  depositeNumber: string
): Promise<void> => {
  const query = graphql(`
    mutation ($project_id: Int!, $deposite_number: String) {
      update_projects_by_pk(
        pk_columns: { id: $project_id }
        _set: { deposite_number: $deposite_number }
      ) {
        id
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
      deposite_number: depositeNumber,
    })
    .then(() => Promise.resolve())
}
export type CityList = Record<
  string,
  {
    nodes: {
      city: string
    }[]
  }
>
export const listCities = (gqlClient: GqlClient): Promise<CityList> => {
  const query = graphql(`
    query {
      projects_aggregate(distinct_on: city) {
        nodes {
          city
        }
      }
    }
  `)
  return gqlClient.request(query)
}
